/**
 * Workshop CalendarFrame — week strip + event list.
 *
 * Props-in, JSX-out. Event rows report their id via onEventClick and
 * execute nothing.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface CalendarDay {
  label: string;
  date: string;
  isToday?: boolean;
}

interface CalendarEvent {
  id: string;
  time: string;
  title: string;
  detail: string;
  color: string;
}

interface CalendarFrameProps {
  days?: CalendarDay[];
  events?: CalendarEvent[];
  status?: FrameStatus;
  onEventClick?: (id: string) => void;
  className?: string;
  "data-testid"?: string;
}

const MOCK_DAYS: CalendarDay[] = [
  { label: "Mon", date: "15" },
  { label: "Tue", date: "16" },
  { label: "Wed", date: "17", isToday: true },
  { label: "Thu", date: "18" },
  { label: "Fri", date: "19" },
  { label: "Sat", date: "20" },
  { label: "Sun", date: "21" },
];

const MOCK_EVENTS: CalendarEvent[] = [
  {
    id: "evt-standup",
    time: "09:30",
    title: "Team standup",
    detail: "Engineering sync · 30 min · Meet",
    color: "#0a84ff",
  },
  {
    id: "evt-lunch",
    time: "12:30",
    title: "Lunch with Maya",
    detail: "Café am Neuen See · 1 h",
    color: "#3af88c",
  },
  {
    id: "evt-dinner",
    time: "19:00",
    title: "Dinner reservation",
    detail: "Nobelhart & Schmutzig · 2 guests · Thu 19:00",
    color: "#ff9f0a",
  },
];

export function CalendarFrame({
  days = MOCK_DAYS,
  events = MOCK_EVENTS,
  status = "done",
  onEventClick,
  className,
  "data-testid": testId = "calendar-frame",
}: CalendarFrameProps) {
  const isEmpty = status === "done" && events.length === 0;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      {...surfaceRoleAttributes("frame")}
      data-testid={testId}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-3 py-2.5">
        {status === "streaming" ? (
          <div data-testid={`${testId}-loading`} aria-label="Loading calendar">
            <div className="mb-2 flex items-center gap-2">
              <Spinner className="h-3 w-3" />
              <span className="text-canvas-xs text-muted">
                Syncing calendar…
              </span>
            </div>
            <div className="grid grid-cols-7 gap-1" aria-hidden="true">
              {Array.from({ length: 7 }).map((_, i) => (
                <div
                  key={i}
                  className="h-10 animate-pulse rounded-control bg-elevated/60"
                />
              ))}
            </div>
            <div className="mt-2 flex flex-col gap-1.5" aria-hidden="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-9 animate-pulse rounded-control bg-elevated/60"
                />
              ))}
            </div>
          </div>
        ) : status === "error" ? (
          <div
            className="flex flex-col items-center gap-1.5 rounded-preview bg-danger-muted px-3 py-6 text-center"
            data-testid={`${testId}-error`}
            role="alert"
          >
            <Icons.CircleAlert
              className="h-4 w-4 text-status-error"
              aria-hidden="true"
            />
            <p className="text-canvas-sm text-secondary">
              Couldn&apos;t load your calendar.
            </p>
            <p className="text-canvas-xs text-muted">
              Check your connection and try again.
            </p>
          </div>
        ) : (
          <>
            <div
              className="grid shrink-0 grid-cols-7 gap-1"
              data-testid={`${testId}-week-strip`}
            >
              {days.map((day) => (
                <div
                  key={day.label}
                  className={cn(
                    "flex flex-col items-center gap-0.5 rounded-control py-1",
                    day.isToday && "bg-accent/10",
                  )}
                >
                  <span className="text-canvas-2xs font-semibold uppercase tracking-wide text-muted">
                    {day.label}
                  </span>
                  <span
                    className={cn(
                      "text-canvas-sm tabular-nums",
                      day.isToday
                        ? "font-bold text-accent"
                        : "font-medium text-secondary",
                    )}
                  >
                    {day.date}
                  </span>
                </div>
              ))}
            </div>

            {isEmpty ? (
              <div
                className="flex flex-col items-center gap-1.5 px-3 py-4 text-center"
                data-testid={`${testId}-empty`}
              >
                <Icons.Calendar
                  className="h-4 w-4 text-muted"
                  aria-hidden="true"
                />
                <p className="text-canvas-sm text-secondary">
                  No events this week.
                </p>
                <p className="text-canvas-xs text-muted">
                  Enjoy the open calendar — or add something below.
                </p>
              </div>
            ) : (
              <div
                className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
                data-testid={`${testId}-event-list`}
              >
                {events.map((event) => (
                  <div
                    key={event.id}
                    data-testid={`${testId}-event-${event.id}`}
                    title={event.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick?.(event.id);
                    }}
                    className="flex min-w-0 shrink-0 cursor-pointer items-center gap-2 rounded-control bg-elevated/60 px-2.5 py-1.5 hover:bg-glass-stroke-faint/20"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: event.color }}
                      aria-hidden="true"
                    />
                    <span className="w-10 shrink-0 font-mono text-canvas-2xs text-muted">
                      {event.time}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-canvas-xs font-medium text-primary">
                        {event.title}
                      </span>
                      <span className="block truncate text-canvas-2xs text-muted">
                        {event.detail}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export type { CalendarFrameProps, CalendarDay, CalendarEvent };
export type { FrameStatus as CalendarStatus };
